import React, { useState, useEffect } from 'react';
import { Box, Button, Stepper, Step, StepButton } from '@mui/material';
import styled from '@emotion/styled';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';

const CarouselContainer = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: '500px',
});

const ContentContainer = styled(Box)({
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '1em 0',
});

const NavigationContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.5em 2em',
    borderTop: `1px solid ${theme.palette.border.divider}`,
    gap: '1em',
}));

const StepperContainer = styled(Box)({
    padding: '1em 2em 0 2em',
});

const StyledButton = styled(Button)(({ theme }) => ({
    borderRadius: '10px',
    padding: '0.5em 1.5em',
    fontFamily: 'Inter, Arial, sans-serif',
    fontWeight: 600,
    fontSize: 'clamp(14px, 1.2vw, 16px)',
    textTransform: 'none',
    minWidth: '120px',
}));

const PrimaryButton = styled(StyledButton)(({ theme }) => ({
    backgroundColor: theme?.palette?.primary?.main || '#0072E4',
    color: 'white',
    '&:hover': {
        backgroundColor: theme?.palette?.primary?.dark || '#0056B3',
    },
}));

const SecondaryButton = styled(StyledButton)(({ theme }) => ({
    backgroundColor: 'transparent',
    color: theme.palette.text.secondary,
    border: `1px solid ${theme.palette.border.divider}`,
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}));

/**
 * Composant Carrousel pour le tutoriel d'onboarding
 * Utilise MUI Stepper pour la navigation entre slides
 * 
 * @param {Array} steps - Tableau de slides [{title, content}]
 * @param {function} onStepChange - Callback appelé quand l'utilisateur change de slide
 * @param {function} onComplete - Callback appelé quand l'utilisateur termine le tutoriel
 */
const OnboardingCarousel = ({ steps = [], onStepChange, onComplete }) => {
    const [activeStep, setActiveStep] = useState(0);
    const maxSteps = steps.length;

    // Ramene l'index dans la plage si le nombre de slides change
    useEffect(() => {
        if (activeStep >= maxSteps && maxSteps > 0) {
            setActiveStep(maxSteps - 1);
        }
    }, [activeStep, maxSteps]);

    // Notifier le parent du changement de slide
    useEffect(() => {
        if (onStepChange) {
            onStepChange(activeStep);
        }
    }, [activeStep, onStepChange]);

    const handleNext = () => {
        if (activeStep < maxSteps - 1) {
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
        } else {
            // Dernière slide : terminer le tutoriel
            if (onComplete) {
                onComplete();
            }
        }
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleStep = (step) => () => {
        setActiveStep(step);
    };

    const isLastStep = activeStep === maxSteps - 1;
    const isFirstStep = activeStep === 0;

    return (
        <CarouselContainer>
            {/* Stepper de progression en haut */}
            <StepperContainer>
                <Stepper activeStep={activeStep} alternativeLabel>
                    {steps.map((step, index) => (
                        <Step key={index}>
                            <StepButton onClick={handleStep(index)} aria-label={step.title}>
                                {/* Pas de label pour garder l'interface épurée */}
                            </StepButton>
                        </Step>
                    ))}
                </Stepper>
            </StepperContainer>

            {/* Contenu de la slide active */}
            <ContentContainer>
                {steps[activeStep]?.content}
            </ContentContainer>

            {/* Navigation bas */}
            <NavigationContainer>
                <SecondaryButton
                    onClick={handleBack}
                    disabled={isFirstStep}
                    startIcon={<KeyboardArrowLeft />}
                >
                    Précédent
                </SecondaryButton>

                {/* Indicateur de progression */}
                <Box
                    sx={{
                        fontFamily: 'Inter, Arial, sans-serif',
                        fontSize: 'clamp(12px, 1vw, 14px)',
                        color: '#666',
                    }}
                >
                    {activeStep + 1} / {maxSteps}
                </Box>

                {isLastStep ? (
                    <PrimaryButton onClick={handleNext}>
                        Terminer
                    </PrimaryButton>
                ) : (
                    <PrimaryButton
                        onClick={handleNext}
                        endIcon={<KeyboardArrowRight />}
                    >
                        Suivant
                    </PrimaryButton>
                )}
            </NavigationContainer>
        </CarouselContainer>
    );
};

export default OnboardingCarousel;
